<script>
    import AppFooter from './AppFooter.svelte';
    import GraphTitle from './GraphTitle.svelte';
    import HorizontalGraph from './HorizontalGraph.svelte';
    import Modal from './Modal.svelte';
    import VerticalGraph from './VerticalGraph.svelte';
    import VizHeader from './VizHeader.svelte';
    import VizTopControls from './VizTopControls.svelte';
    import InfoIcon from './InfoIcon.svelte';

    import { onMount } from 'svelte';
    import _ from 'lodash';
    import loadData from '../utils/load_data.js';
    import loadMetaData from '../utils/load_meta_data.js';
    import { icon_folder } from '../inputs/data_paths';
    import { 
        getAgeDropdownKey, getAgeKey, getDataPointKey,
        getMaxFromDataPoints, getMinFromDataPoints, getNiceDomain,
        mapPointsToErrorStrings, parseRegionDataHorizontal, parseRegionDataVertical, 
        sortAgesBySection 
    } from '../utils/utils.js';

    let loaded = false, data, meta_data;
    let chemical_group_options, selected_chemical_group;
    let chemical_substance_options, selected_chemical_substance;
    let survey_cycle_options, selected_survey_cycle;
    let matrix_options, selected_matrix;
    let age_options, selected_age;
    let midpoints = [
        { key: 'GeometricMean', title: 'Geometric mean' },
        { key: 'Median', title: 'Median' }
    ];
    let scales = [
        { key: 'recommended', title: 'Biomonitoring guidance value' },
        { key: 'data', title: 'Data' }
    ];
    let midpoint = "Median", scale = "data";
    let prairie_only_fields = false;
    let year, unit = null, rec_limit;
    let help_modal, info_modal, prairie_filter_modal;
    let info_overview = '', info_health = '', info_more = '', info_website_source = '', info_rec_limit = null;

    const horizontal_graph_margin = {
        top: 30,
        bottom: 20,
        left: 100
    };
    const vertical_graph_margin = {
        top: 60,
        bottom: 20,
        left: 40
    };
    let visualization_width = window.innerWidth;
    $: {
        if (visualization_width < 1198) {
            horizontal_graph_margin.right = 10;
            vertical_graph_margin.right = 10;
        } else {
            horizontal_graph_margin.right = 20;
            vertical_graph_margin.right = 30;
        }
    }

    onMount(async () => {
        // load the datasets
        data = (await loadData());
        meta_data = (await loadMetaData());

        chemical_group_options = prairie_only_fields ? Object.keys(data.Prairies) : Object.keys(data.Canada);
        selected_chemical_group = chemical_group_options[0];

        loaded = true;
    });

    // update selected substances, cycles, matrices and ages when prairie_only_fields changes
    $: {
        if (loaded) {
            resetGroups({ prairie_only_fields, try_no_change: true });
            resetSubstances({ prairie_only_fields, try_no_change: true });
            resetMatrices({ prairie_only_fields, try_no_change: true });
            resetCycles({ prairie_only_fields, try_no_change: true });
            resetAges({ prairie_only_fields, try_no_change: true });
        }
    }

    // update selected substances, cycles, matrices and ages when chemical_group changes
    $: {
        if (loaded) {
            resetSubstances({ chemical_group: selected_chemical_group });
            resetMatrices({ chemical_group: selected_chemical_group });
            resetCycles({ chemical_group: selected_chemical_group });
            resetAges({ chemical_group: selected_chemical_group });
        }
    }

    // update selected cycles, matrices and ages when chemical_substance changes
    $: {
        if (loaded) {
            resetMatrices({ chemical_substance: selected_chemical_substance });
            resetCycles({ chemical_substance: selected_chemical_substance });
            resetAges({ chemical_substance: selected_chemical_substance })
        }
    }

    // update selected cycles and ages when matrix changes (keep same cycle and age if possible)
    $: {
        if (loaded) {
            resetCycles({ matrix: selected_matrix, try_no_change: true });
            resetAges({ matrix: selected_matrix, try_no_change: true })
        }
    }

    let domain_data, domain;
    $: {
        if (loaded) {
            // extract data that fits all selected criteria (except age and cycle)
            domain_data = _.mapValues(data, (region_data) => {
                return _(_.get(region_data, [selected_chemical_group, selected_chemical_substance, selected_matrix.title]), {})
                    // map cycle to array of data_points
                    .map((cycle) => _.values(cycle))
                    .flatten()
                    .value();
            });

            const flattened_data = _(domain_data).map((data_points) => data_points).flatten().value();

            // extract meta_data from first point that fits all selected_data
            // for year and unit just take year of first data_point and assume 
            // it's the same for all values
            const first_data_point = _.find(flattened_data, (data_point) => data_point.Cycle === selected_survey_cycle);
            year = first_data_point.Year;
            unit = first_data_point.Units;
            // extract rec_limit and description from meta_data
            let point_meta_data = meta_data[getDataPointKey(first_data_point)];
            rec_limit = _.get(point_meta_data, 'RecLimit', NaN);
            info_overview = _.get(point_meta_data, 'Overview', '');
            info_health = _.get(point_meta_data, 'HealthConsiderations', ''); 
            info_more = _.get(point_meta_data, 'MoreInformation', ''); 
            info_website_source = _.get(point_meta_data, 'WebsiteSource', '');
            info_rec_limit = _.get(point_meta_data, 'RecLimitInfo');

            // get limit of detection
            let lods = _.map(flattened_data, 'LOD');
            let lod = flattened_data.length ? _.min(lods) : null;

            // get domain for all data_points
            // NOTE: max must be at least 10x LOD
            let max;
            if (scale === 'recommended' && _.isNumber(rec_limit)) {
                let data_max = getMaxFromDataPoints(flattened_data, midpoint);
                max = _.max([data_max, rec_limit, lod ? lod * 10 : 0]);
            } else {
                max = _.max([getMaxFromDataPoints(flattened_data, midpoint), lod ? lod * 10 : 0]); 
            }
            let min;
            if (scale === 'recommended' && _.isNumber(lod)) {
                let data_min = getMinFromDataPoints(flattened_data, midpoint);
                min = _.min([data_min, lod]);;
            } else {
                min = getMinFromDataPoints(flattened_data, midpoint);
            }
            domain = getNiceDomain(min, max);
        }
    }

    let horizontal_selected_data, vertical_selected_data;
    let horizontal_selected_errors, vertical_selected_errors;
    // update selected_data
    $: {
        if (loaded) {
            horizontal_selected_data = _.mapValues(domain_data, (region_data) => {
                return parseRegionDataHorizontal(
                    _.filter(region_data, (data_point) => data_point.Cycle === selected_survey_cycle || data_point.Cycle === 'active'), midpoint, selected_survey_cycle);
            });
            horizontal_selected_errors = _(_.concat(horizontal_selected_data.Prairies, horizontal_selected_data.Canada))
                .groupBy('static')
                .mapValues((data_points) => {
                    return mapPointsToErrorStrings(data_points, selected_chemical_substance, selected_survey_cycle, 
                            selected_matrix, selected_age, midpoint);
                })
                .value();
            vertical_selected_data = _.mapValues(domain_data, (region_data) => {
                return parseRegionDataVertical(_.filter(region_data, (data_point) => { 
                    return getAgeKey(data_point) === _.get(selected_age, 'title', '')
                }), midpoint, selected_survey_cycle);
            });
            vertical_selected_errors = _(_.concat(vertical_selected_data.Prairies, vertical_selected_data.Canada))
                .groupBy('static')
                .mapValues((data_points) => {
                    return mapPointsToErrorStrings(data_points, selected_chemical_substance, selected_survey_cycle, 
                            selected_matrix, selected_age, midpoint);
                })
                .value();
        }
    }

    const resetGroups = (params={}) => {
        const prairie_only = _.get(params, 'prairie_only_fields', prairie_only_fields);
        const try_no_change = _.get(params, 'try_no_change', false);

        // update chemical_group_options
        chemical_group_options = _.sortBy(prairie_only ? Object.keys(data.Prairies) : Object.keys(data.Canada));
        if(!try_no_change || !chemical_group_options.includes(selected_chemical_group))
            selected_chemical_group = _.get(chemical_group_options, 0, 'No Chemical Group');
    };
    const resetSubstances = (params={}) => {
        const chemical_group = _.get(params, 'chemical_group', selected_chemical_group);
        const prairie_only = _.get(params, 'prairie_only_fields', prairie_only_fields);
        const try_no_change = _.get(params, 'try_no_change', false);

        // update chemical_substance_options
        chemical_substance_options = _.sortBy(prairie_only ? Object.keys(data.Prairies[chemical_group]) : Object.keys(data.Canada[chemical_group]));
        if(!try_no_change || !chemical_substance_options.includes(selected_chemical_substance)) 
            selected_chemical_substance = _.get(chemical_substance_options, 0, 'No Chemical Substances');
    };
    const resetMatrices = (params={}) => {
        const chemical_group = _.get(params, 'chemical_group', selected_chemical_group);
        const chemical_substance = _.get(params, 'chemical_substance', selected_chemical_substance);
        const prairie_only = _.get(params, 'prairie_only_fields', prairie_only_fields);
        const try_no_change = _.get(params, 'try_no_change', false);

        // update matrix_options
        const matrix_keys = _.sortBy(prairie_only
            ? Object.keys(data.Prairies[chemical_group][chemical_substance]) 
            : Object.keys(data.Canada[chemical_group][chemical_substance]));
        matrix_options = _.map(matrix_keys, (matrix_option) => {
            return {
                title: matrix_option,
                selected_img: `${icon_folder}images/selected-${matrix_option.replaceAll(' ','_').toLowerCase()}-icon.svg`,
                unselected_img: `${icon_folder}images/unselected-${matrix_option.replaceAll(' ','_').toLowerCase()}-icon.svg`,
                alt: `${matrix_option} icon`
            };
        });
        if(!try_no_change || !_.map(matrix_options, 'title').includes(_.get(selected_matrix, 'title', null))) 
            selected_matrix = _.get(matrix_options, 0);
    };
    const resetCycles = (params={}) => {
        const chemical_group = _.get(params, 'chemical_group', selected_chemical_group);
        const chemical_substance = _.get(params, 'chemical_substance', selected_chemical_substance);
        const matrix = _.get(params, 'matrix', selected_matrix);
        const prairie_only = _.get(params, 'prairie_only_fields', prairie_only_fields);
        const try_no_change = _.get(params, 'try_no_change', false);

        // update survey_cycle_options (prairie_only doesn't effect available cycles)
        // only using CanadaData for available cycles since all prairie data is assumed to have selected_cycle "active"
        survey_cycle_options = _.sortBy(
            _.uniq(Object.keys(_.get(data, ['Canada', chemical_group, chemical_substance, matrix.title], [])))
        );
        if(!try_no_change || !survey_cycle_options.includes(selected_survey_cycle))
            selected_survey_cycle = _.max(survey_cycle_options);
    };
    const resetAges = (params={}) => {
        const chemical_group = _.get(params, 'chemical_group', selected_chemical_group);
        const chemical_substance = _.get(params, 'chemical_substance', selected_chemical_substance);
        const matrix = _.get(params, 'matrix', selected_matrix);
        const try_no_change = _.get(params, 'try_no_change', false);

        // update age_options
        // grab all age_options from all cycles
        let age_option_titles = new Set()
        age_options = [];
        const data_points = _.concat(
            _.values(_.get(data, ['Canada', chemical_group, chemical_substance, matrix.title], {})),
            _.values(_.get(data, ['Prairies', chemical_group, chemical_substance, matrix.title], {}))
        )
        _(data_points).forEach((ages_dict) => {
            _.forEach(_.values(ages_dict), (d) => {
                let age_title = getAgeKey(d);
                if(age_option_titles.has(age_title)) return;

                age_option_titles.add(age_title)
                age_options.push({ section: d.Section, group: d.Group, title: getAgeKey(d), dropdown: getAgeDropdownKey(d) });
            });
        });
        age_options = sortAgesBySection(age_options, 'group');
        if(!try_no_change || !_.map(age_options, 'title').includes(_.get(selected_age, 'title', ''))) 
            selected_age = _.get(age_options, '0');
    };
</script>

<div id="visualization" bind:clientWidth={visualization_width}>
    <VizHeader />
    {#if loaded}
        <VizTopControls
            chemical_group_options={chemical_group_options}
            chemical_substance_options={chemical_substance_options}
            survey_cycle_options={survey_cycle_options}
            matrix_options={matrix_options}
            current_year={year}
            {midpoints}
            {scales}
            show_help_modal={() => help_modal.show()}
            show_prairie_filter_modal={() => prairie_filter_modal.show()}
            bind:selected_chemical_group={selected_chemical_group}
            bind:selected_chemical_substance={selected_chemical_substance}
            bind:selected_survey_cycle={selected_survey_cycle}
            bind:selected_matrix={selected_matrix}
            bind:prairie_only_fields={prairie_only_fields}
            bind:midpoint={midpoint}
            bind:scale={scale}
        />
        <div id="visualization__content">
            <div id="visualization__graphs-title">
                <div id="visualization__graphs-title__substance">
                    {selected_chemical_substance}
                </div>
            </div>
            <div id="visualization__graphs">
                <div class="visualization__graph">
                    <GraphTitle
                        id="vertical-graph-title"
                        title={`${selected_matrix.title} concentrations by cycle for`}
                        dropdown_options={_.map(age_options, (option) => {
                            return { title: option.dropdown, value: option.title }
                        })}
                        dropdown_available_options={_.map(age_options, 'title')}
                        bind:dropdown_selected={selected_age.title}
                    />
                    <VerticalGraph 
                        data={vertical_selected_data}
                        data_errors={vertical_selected_errors}
                        margin={vertical_graph_margin}
                        {unit}
                        {rec_limit}
                        source={info_website_source}
                        {scales}
                        y_domain={domain}
                        {selected_survey_cycle}
                        bind:scale={scale}
                    />
                </div>
                <div class="visualization__graph">
                    <GraphTitle
                        id="horizontal-graph-title"
                        title={`${selected_matrix.title} concentrations by demographics for survey`}
                        dropdown_options={[
                            { title: "Cycle 1", value: "1" }, 
                            { title: "Cycle 2", value: "2" },
                            { title: "Cycle 3", value: "3" },
                            { title: "Cycle 4", value: "4" },
                            { title: "Cycle 5", value: "5" },
                            { title: "Cycle 6", value: "6" }
                        ]}
                        dropdown_available_options={survey_cycle_options}
                        bind:dropdown_selected={selected_survey_cycle}
                    />
                    <HorizontalGraph 
                        data={horizontal_selected_data}
                        data_errors={horizontal_selected_errors}
                        margin={horizontal_graph_margin}
                        {unit}
                        {rec_limit}
                        source={info_website_source}
                        chemical_substance={selected_chemical_substance}
                        {scales}
                        x_domain={domain}
                        selected_age={_.get(selected_age, 'title', '')}
                        bind:scale={scale}
                    />
                </div>
            </div>
        </div>
        <AppFooter />
        <Modal
            bind:this={prairie_filter_modal}
        >
            <div id="visualization__prairie-filter-modal">
                <div id="visualization__prairie-filter-modal-title">Prairies data filter</div>
                <div>The prairies data filter shows the average concentration of a substance across survey cycles 1-5 in Alberta, Saskatchewan, and Manitoba, for substances where data is available.</div>
            </div>
        </Modal>
        <Modal
            bind:this={help_modal}
        >
            <div id="visualization__help-modal">
                <div id="visualization__help-modal-title">How to read the charts</div>
                <img
                    src={`${icon_folder}images/how-to-read-${midpoint.toLowerCase().replace(' ','')}.svg`}
                    alt="How to read image"
                />
            </div>
        </Modal>
        <Modal
            background_dark={false}
            bind:this={info_modal}
        >
            <div id="visualization__info-modal">
                <div class="visualization__info-modal__section">
                    <div class="visualization__info-modal__section-header">Overview</div>
                    <div class="visualization__info-modal__section-content">
                        {@html info_overview}
                    </div>
                </div>
                <div class="visualization__info-modal__section">
                    <div class="visualization__info-modal__section-header">Health considerations</div>
                    <div class="visualization__info-modal__section-content">
                        {@html info_health}
                    </div>
                </div>
                {#if info_rec_limit}
                    <div class="visualization__info-modal__section">
                        <div class="visualization__info-modal__section-header">Biomonitoring guidance value information</div>
                        <div class="visualization__info-modal__section-content">
                            {@html info_rec_limit}
                        </div>
                    </div>
                {/if}
                <div class="visualization__info-modal__section">
                    <div class="visualization__info-modal__section-header">For more information</div>
                    <div class="visualization__info-modal__section-content">
                        {@html info_more}
                    </div>
                </div>
            </div>
        </Modal>
    {:else}
        <div>Loading...</div>
    {/if}
</div>

<style>
    #visualization {
        padding: 16px calc(50% - 574px);
    }
    #visualization__content {
        display: flex;
        flex-direction: column;
        margin-top: 16px;
    }
    #visualization__graphs {
        display: flex;
    }
    #visualization__graphs-title {
        display: flex;
        justify-content: space-between;
        align-items: flex-end;
        max-width: 512px;
        padding-bottom: 8px;
    }
    #visualization__graphs-title__substance {
        color: #0D7D9B;
        font-size: 20px;
        font-weight: 700;
    }
    .visualization__graph {
        display: flex;
        flex-direction: column;
        padding-bottom: 10px;
    }
    #visualization__help-modal,
    #visualization__prairie-filter-modal {
        margin: 16px 32px;
    }
    #visualization__help-modal-title,
    #visualization__prairie-filter-modal-title {
        color: #333;
        font-size: 15px;
        font-weight: 700;
        margin-bottom: 32px;
    }
    #visualization__info-modal {
        display: flex;
        flex-direction: column;
        padding: 16px 32px;
    }
    .visualization__info-modal__section:not(:first-child) {
        padding-top: 8px;
    }
    .visualization__info-modal__section-header {
        color: #444;
        font-size: 15px;
        font-weight: 700;
    }
    .visualization__info-modal__section-content {
        color: #777;
        font-size: 16px;
        padding-top: 4px;
    }
    @media screen and (min-height: 1000px) {
        #visualization {
            padding: 32px calc(50% - 574px);
        }
    }
    @media screen and (min-width: 1199px) {
        .visualization__graph:last-child {
            border-left: 1px solid #CCC;
            padding-left: 30px;
        }
    }
    @media screen and (max-width: 1198px) {
        #visualization {
            padding: 48px;
        }
        #visualization__graphs {
            flex-direction: column-reverse;
        }
        .visualization__graph:not(:first-of-type) {
            margin-bottom: 32px;
        }
        #visualization__info-modal {
            box-sizing: border-box;
            width: calc(100vw - 64px);
        }
    }
    @media screen and (max-width: 724px) {
        #visualization {
            padding: 32px;
        }
        #visualization__help-modal {
            width: calc(100vw - 32px);
        }
        #visualization__info-modal {
            box-sizing: border-box;
            max-height: calc(100vh - 64px);
            overflow: scroll;
            width: calc(100vw - 48px);
        }
    }
</style>